import { selectContentClickHandler } from '@mnd-apps/public/lib/tracker';
import Button from '@mnd-frontend/ui/public/components/Button';
import { CardBaseStyle } from '@mnd-frontend/ui/public/components/CardBase';
import { formatUrl } from '@mnd-frontend/ui/public/utils/formatters/url';
import {
  BREAKPOINTS,
  RADIUS_XL,
  SPACING_1,
  SPACING_2,
  SPACING_3,
  WEB_RED_REG,
} from '@mnd-frontend/ui/shared/tokens';
import { transformStringToSnakeCase } from '@mnd-frontend/utils';
import { memo, type RefObject } from 'react';
import styled from 'styled-components';
import type { TransformedCardProps } from './BlogCardGridModule';
import BlogCardImage from './BlogCardImage';

const BlogCardWrapper = styled.li`
  ${CardBaseStyle}

  background-color: #F7F7F9;
  width: calc(96vw - (${SPACING_2} * 2));
  scroll-snap-align: center;
  margin: 0 ${SPACING_1};

  @media (min-width: ${BREAKPOINTS.XS}px){
    width: calc(80vw - (${SPACING_2} * 2));
  }

  @media (min-width: ${BREAKPOINTS.MD}px){
    width: auto;
  }

  @media (min-width: ${BREAKPOINTS.XL}px){
    margin: 0;
  }
`;

const BlogCardBodyContainer = styled.div`
    padding: ${SPACING_2};
    display: grid;
    grid-template-rows: 2rem minmax(auto, 10.75rem) auto;

    @media (min-width: ${BREAKPOINTS.MD}px){
      padding: ${SPACING_3};
    }

    .kicker {
      color: ${WEB_RED_REG};
      // token: web/overline
      font-family: Silka;
      font-size: 0.875rem;
      font-style: normal;
      font-weight: 500;
      display: block;
    }

    .heading {
      margin: 0;
      // token: web/subheading/desktop
      font-size: 1.125rem;
      font-weight: 500;
      line-height: 1.25rem;
      letter-spacing: -0.03375rem;
      margin-bottom: ${SPACING_1};

      @media (min-width: ${BREAKPOINTS.MD}px){
        font-size: 1.375rem;
        line-height: 1.65rem;
        letter-spacing: -0.04125rem;
      }
    }

    .text {
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 5;
      // token: web/body/sm
      font-size: 0.875rem;
      font-style: normal;
      font-weight: 400;
      line-height: 1.3125rem;

      @media (min-width: ${BREAKPOINTS.MD}px){
        font-size: 0.875rem;
      }
    }

    .link {
      display: inline-block;
      margin-top: ${SPACING_2};
      border-radius: ${RADIUS_XL}; /* For focus outline */
      width: fit-content;
    }
`;

type BlogCardProps = TransformedCardProps & {
  id: string;
  hideFromScreenReader?: boolean /* For mobile where slider is applied. */;
  ref?: RefObject<HTMLLIElement>;
};

// #region Main Component
const BlogCard = ({
  id,
  headline,
  description,
  image,
  kicker,
  logo,
  link,
  hideFromScreenReader,
  ref,
}: BlogCardProps) => (
  <BlogCardWrapper
    {...(ref ? { ref } : {})}
    {...(hideFromScreenReader ? { 'aria-hidden': hideFromScreenReader } : {})}
  >
    <BlogCardImage image={image} logo={logo} />
    <BlogCardBodyContainer>
      <span className="kicker" aria-hidden={!kicker}>
        {kicker || ''}
      </span>
      <article>
        <h3 id={id} className="heading">
          {headline}
        </h3>
        <p className="text">{description}</p>
      </article>
      {link?.url && link?.title && (
        <Button
          className="link"
          href={formatUrl(link.url)}
          newTab={link?.target === '_blank'}
          configuration="ghost"
          aria-labelledby={id}
          onClick={selectContentClickHandler(
            transformStringToSnakeCase(headline ?? ''),
            'blog-card',
          )}
        >
          {link.title}
        </Button>
      )}
    </BlogCardBodyContainer>
  </BlogCardWrapper>
);
// #endregion

export default memo(BlogCard);
